import { quantityReceive } from '@/resources/components/cart/reviewItemModal/QuantityInput';
import { toSchedulesFormat } from '@/services/dates';
import { revertMoneyMaskToFloat } from '@/services/global';
import { getAnswerType, insertSelectOption } from '@/services/options';
import { ICartItemRequest, ICartItemValues, ICartProduct } from '@/store/ducks/order/types';
import { IOptionGroup, IQuestion, ISchedule, IVisualSchedule } from '@/store/ducks/products/types';

export const generateFormOptions = (
  action: 'add' | 'receive',
  cartItem: ICartProduct | null | undefined,
  optionsGroup: IOptionGroup | null,
) => {
  const generateForAdd = (optionsGroup: IOptionGroup) => {
    const question = optionsGroup.options[0];

    return question
      ? [
          {
            select_options: question.select_options.map((answer) =>
              insertSelectOption({ ...answer, chosen: answer.marked_as_default }, question),
            ),
            add_order_quantity: question.add_order_quantity,
          },
        ]
      : [];
  };

  const generateForReceive = (optionsGroup: IOptionGroup) => {
    const question: any[] = [];

    optionsGroup.options.forEach((option) => {
      question.push({
        select_options: option.select_options.map((answer) => {
          const selectedOption = cartItem?.options.find(
            (selectedOption) => selectedOption.id === answer.id,
          );

          return selectedOption
            ? insertSelectOption(
                { ...answer, chosen: true, quantity: selectedOption.quantity },
                option,
              )
            : insertSelectOption({ ...answer, chosen: false }, option);
        }),
        add_order_quantity: option.add_order_quantity,
      });
    });

    return question;
  };

  if (optionsGroup && optionsGroup.options.length > 0 && action === 'add')
    return generateForAdd(optionsGroup);

  if (optionsGroup && optionsGroup.options.length > 0 && action === 'receive')
    return generateForReceive(optionsGroup);

  return [];
};

export const normalizeForm = (values: ICartItemValues, options: IQuestion[]): ICartItemRequest => {
  const addOrderQuantity = values.options.some((option) => option.add_order_quantity);

  const form: ICartItemRequest = {
    options: [],
    ean: values.ean,
    product_id: values.product_id!,
    quantity: revertMoneyMaskToFloat(values.quantity),
    uid: values.uid,
  };

  values.options.forEach((option, index) => {
    const fullOption = options[index];

    if (fullOption) {
      const type = getAnswerType(fullOption);

      option.select_options.forEach((answer) => {
        if (type === 'additionals') {
          if (parseInt(answer.quantity) > 0) answer.chosen = true;
          else answer.chosen = false;
        }

        if (answer.chosen) {
          answer.quantity = parseInt(answer.quantity);
          form?.options?.push?.(answer);
        }

        if (+values.quantity === 0) {
          answer.quantity = 0;
        }
      });
    }
  });

  const sumQuantity = form?.options?.reduce?.((accumulator, currentValue) => {
    return accumulator + currentValue.quantity;
  }, 0);

  if (addOrderQuantity && sumQuantity !== 0) form.quantity = 1;

  return form;
};

export const normalizeFormValues = (
  values: ICartItemValues,
  action: 'add' | 'receive',
  getInitialQuantity: () => number,
  currentMeasure: 'kg' | 'unity',
  seeAsUnit: boolean,
  optionsGroup: IOptionGroup | null,
) => {
  const quantity = action === 'add' ? getInitialQuantity().toString() : values.quantity;

  values.quantity = quantityReceive(quantity, currentMeasure, seeAsUnit).toString();

  const options = optionsGroup ? optionsGroup.options : [];

  return normalizeForm(values, options);
};

export const getInitialFormValues = (
  product: any,
  initialQuantity: string | number,
  currentMeasure: 'kg' | 'unity',
  action: 'add' | 'receive',
  cartItem: ICartProduct | null,
  optionsGroup: IOptionGroup | null,
): ICartItemValues => {
  return {
    ean: product.ean,
    quantity: `${initialQuantity}${currentMeasure === 'kg' && !product.see_as_unit ? ' KG' : ''}`,
    product_id: product.product_id,
    options: generateFormOptions(action, cartItem, optionsGroup),
    see_as_unit: product.see_as_unit,
    average_weight: product.average_weight,
    uid: cartItem?.uid,
  } as ICartItemValues;
};

export const getScheduleByDay = (schedule: ISchedule | undefined): IVisualSchedule => {
  if (!schedule) return { isAvailable: true };

  const currentDate = new Date();
  const now = {
    day: toSchedulesFormat[
      currentDate.toString().split(' ')[0]?.toLowerCase() as keyof typeof toSchedulesFormat
    ],
    hour: currentDate.getHours(),
    minute: currentDate.getMinutes(),
  };

  const productScheduleState: IVisualSchedule = {};
  const todaySchedule = schedule[now.day as keyof ISchedule];

  if (!todaySchedule) {
    productScheduleState.isAvailable = false;
  } else {
    const currentTimeInMinutes = now.hour * 60 + now.minute;
    const timeRanges = todaySchedule.split(',');

    const isWithinTimeRange = timeRanges.some((range: string) => {
      const [startTime, endTime] = range.split('-');
      if (!startTime || !endTime) return false;

      const startParts = startTime.split(':').map(Number);
      const endParts = endTime.split(':').map(Number);

      const startHour = startParts[0];
      const startMin = startParts[1];
      const endHour = endParts[0];
      const endMin = endParts[1];

      if (
        startHour === undefined ||
        startMin === undefined ||
        endHour === undefined ||
        endMin === undefined
      ) {
        return false;
      }

      const startTimeInMinutes = startHour * 60 + startMin;
      const endTimeInMinutes = endHour * 60 + endMin;

      if (endTimeInMinutes < startTimeInMinutes) {
        return (
          currentTimeInMinutes >= startTimeInMinutes || currentTimeInMinutes <= endTimeInMinutes
        );
      }

      return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes <= endTimeInMinutes;
    });

    productScheduleState.isAvailable = isWithinTimeRange;
  }

  Object.values(toSchedulesFormat).forEach((dayKey) => {
    const daySchedule = schedule[dayKey as keyof ISchedule];

    if (!daySchedule) {
      productScheduleState[dayKey as keyof IVisualSchedule] = '--';
      return;
    }

    const timeRanges = daySchedule.split(',');
    const formattedRanges = timeRanges
      .map((range: string) => range.split('-').join(' até '))
      .filter((range: string) => range.trim())
      .join(', ');

    productScheduleState[dayKey as keyof IVisualSchedule] = formattedRanges || '--';
  });

  return productScheduleState;
};
